import React from 'react';
import { Box, Typography, Card, Avatar, Chip } from '@mui/joy';
import { Droppable } from '@hello-pangea/dnd';
import TaskCard from './TaskCard';

const StaffColumn = ({ 
  staffMember, 
  tasks, 
  onTaskClick, 
  onDragEnd: _onDragEnd, // eslint-disable-line no-unused-vars
  canManageTasks, 
  isUnassigned = false 
}) => {
  // Calculate workload indicators
  const highPriorityCount = tasks.filter(task => task.priority === '1').length; // Priority 1 is high priority
  const overdueTasks = tasks.filter(task => {
    if (!task.due_date) return false;
    return new Date(task.due_date) < new Date();
  }).length;

  // Get staff member initials
  const getInitials = (name) => {
    if (!name) return 'UN';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2);
  };

  return (
    <Card
      sx={{
        width: 300,
        minWidth: 300,
        maxWidth: 300,
        height: 600, // Fixed height for all columns
        p: 0,
        border: '1px solid',
        borderColor: 'neutral.200',
        borderRadius: 'md',
        overflow: 'hidden', // Hide overflow outside the card
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Column Header */}
      <Box
        sx={{
          p: 2,
          borderBottom: '1px solid',
          borderColor: 'neutral.200',
          background: isUnassigned 
            ? 'linear-gradient(135deg, rgba(108, 117, 125, 0.1) 0%, rgba(73, 80, 87, 0.1) 100%)'
            : 'linear-gradient(135deg, rgba(0, 168, 132, 0.1) 0%, rgba(0, 126, 51, 0.1) 100%)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
          <Avatar 
            size="sm"
            sx={{ 
              bgcolor: isUnassigned ? 'neutral.400' : 'success.500',
              color: 'white'
            }}
          >
            {getInitials(staffMember.display_name)}
          </Avatar>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography 
              level="title-sm" 
              sx={{ 
                color: isUnassigned ? 'neutral.700' : 'success.700',
                fontWeight: 600,
                noWrap: true
              }}
            >
              {isUnassigned ? 'Unassigned' : staffMember.display_name}
            </Typography>
            {!isUnassigned && staffMember.job_title && (
              <Typography 
                level="body-xs" 
                sx={{ color: 'neutral.500', noWrap: true }}
              >
                {staffMember.job_title}
              </Typography>
            )}
          </Box>
        </Box>

        {/* Workload Indicators */}
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Chip 
            size="sm" 
            variant="soft" 
            color="neutral"
            sx={{ fontSize: '0.75rem' }}
          >
            {tasks.length} tasks
          </Chip>
          
          {highPriorityCount > 0 && (
            <Chip 
              size="sm" 
              variant="soft" 
              color="danger"
              sx={{ fontSize: '0.75rem' }}
              startDecorator={
                <span className="material-symbols-outlined" style={{ fontSize: '12px' }}>
                  priority_high
                </span>
              }
            >
              {highPriorityCount}
            </Chip>
          )}
          
          {overdueTasks > 0 && (
            <Chip 
              size="sm" 
              variant="soft" 
              color="warning"
              sx={{ fontSize: '0.75rem' }}
              startDecorator={
                <span className="material-symbols-outlined" style={{ fontSize: '12px' }}>
                  schedule
                </span>
              }
            >
              {overdueTasks} overdue
            </Chip>
          )}
        </Box>
      </Box>

      {/* Tasks List */}
      <Droppable droppableId={isUnassigned ? 'unassigned' : staffMember.id}>
        {(provided, snapshot) => (
          <Box
            ref={provided.innerRef}
            {...provided.droppableProps}
            sx={{
              p: 1,
              flex: 1,
              minHeight: 0,
              maxHeight: '100%',
              overflowY: 'auto',
              backgroundColor: snapshot.isDraggingOver ? 'success.25' : 'transparent',
              transition: 'background-color 0.2s ease',
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
            }}
          >
            {tasks.length === 0 ? (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: 200,
                  color: 'neutral.400',
                  textAlign: 'center',
                }}
              >
                <span 
                  className="material-symbols-outlined" 
                  style={{ fontSize: '48px', marginBottom: '8px' }}
                >
                  {isUnassigned ? 'assignment' : 'task_alt'}
                </span>
                <Typography level="body-sm">
                  {isUnassigned ? 'No unassigned tasks' : 'No tasks assigned'}
                </Typography>
              </Box>
            ) : (
              tasks.map((task, index) => (
                <TaskCard
                  key={task.id}
                  task={task}
                  index={index}
                  onClick={() => onTaskClick(task)}
                  canManageTasks={canManageTasks}
                  sx={{ mb: 1, minHeight: 160, maxHeight: 180 }}
                />
              ))
            )}
            {provided.placeholder}
          </Box>
        )}
      </Droppable>
    </Card>
  );
};

export default StaffColumn;